import React, { useEffect, useRef } from 'react';

import DatePickerFormField from '@components/form-fields/DatePickerFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import TextInputFormField from '@components/form-fields/TextInputFormField';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';

import { useGetBusinessPartnerPersons } from '../amendment.slice';
import { useInsuredDetailsLovs } from '../hooks';

import { useFormikContext } from 'formik';
import { makeStyles } from 'tss-react/mui';

type FormValues = {
  insureds?: Array<{ id: string }>;
  insuredDetails: {
    gender: string;
    dateOfBirth: string;
    nationalityID: string;
    occupationClassID: string;
    profession: string;
    passportNumber: string;
  };
};

type Props = {
  disabledForm: boolean;
  primaryBPId: string;
};

const useStyles = makeStyles()(() => ({
  fieldRowFour: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: 12,
    marginBottom: 12
  },
  fieldRowTwo: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 12,
    marginBottom: 12
  }
}));

const InsuredDetailsWidgetAtom: React.FC<Props> = ({
  disabledForm,
  primaryBPId
}) => {
  const { classes } = useStyles();
  const { genderOptions, nationalityOptions, occupationClassOptions } =
    useInsuredDetailsLovs();

  const { values, errors, touched, setFieldValue, handleBlur } =
    useFormikContext<FormValues>();

  const [personsQuery] = useGetBusinessPartnerPersons();

  const firstInsuredId = values.insureds?.[0]?.id || '';

  const hasPopulatedRef = useRef(false);
  const lastPopulatedIdRef = useRef<string>('');

  useEffect(() => {
    if (!firstInsuredId || !primaryBPId) {
      return;
    }

    const sameInsured = lastPopulatedIdRef.current === firstInsuredId;
    const insuredDetailsAreEmpty =
      !values.insuredDetails?.passportNumber &&
      !values.insuredDetails?.profession &&
      !values.insuredDetails?.dateOfBirth;

    // If amendment form reinitializes and clears fields, allow hydration again.
    if (hasPopulatedRef.current && sameInsured && !insuredDetailsAreEmpty) {
      return;
    }

    (async () => {
      try {
        const res = await personsQuery({
          variables: {
            mainbusinessPartnerID: primaryBPId,
            searchKeyword: ''
          }
        });

        const persons = res?.data?.salesforce?.getBusinessPartnerPersons || [];
        const person = persons.find(
          (p: any) => String(p.id) === String(firstInsuredId)
        );

        if (person) {
          await Promise.all([
            setFieldValue('insuredDetails.gender', person.gender || '', false),
            setFieldValue(
              'insuredDetails.dateOfBirth',
              person.dateOfBirth || '',
              false
            ),
            setFieldValue(
              'insuredDetails.nationalityID',
              person.nationalityId || '',
              false
            ),
            setFieldValue(
              'insuredDetails.occupationClassID',
              person.occupationClass || '',
              false
            ),
            setFieldValue(
              'insuredDetails.profession',
              person.profession || '',
              false
            ),
            setFieldValue(
              'insuredDetails.passportNumber',
              person.passportNumber || '',
              false
            )
          ]);

          hasPopulatedRef.current = true;
          lastPopulatedIdRef.current = firstInsuredId;
        } else {
          hasPopulatedRef.current = false;
          lastPopulatedIdRef.current = '';
        }
      } catch (error) {
        hasPopulatedRef.current = false;
        lastPopulatedIdRef.current = '';
        console.error('Error fetching person details:', error);
      }
    })();
  }, [
    firstInsuredId,
    primaryBPId,
    values.insuredDetails?.passportNumber,
    values.insuredDetails?.profession,
    values.insuredDetails?.dateOfBirth
  ]);

  const insuredDetails = values.insuredDetails;

  return (
    <WidgetPaper>
      <WidgetSection title="Insured Details">
        <div className={classes.fieldRowFour}>
          <DatePickerFormField
            name="insuredDetails.dateOfBirth"
            title="Date of Birth*"
            placeholder="Date of Birth"
            value={insuredDetails.dateOfBirth}
            error={
              touched?.insuredDetails?.dateOfBirth
                ? (errors as any)?.insuredDetails?.dateOfBirth
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onDateChange={(v) =>
              setFieldValue('insuredDetails.dateOfBirth', v, true)
            }
            disabled
            material
          />

          <SelectFormField
            name="insuredDetails.nationalityID"
            title="Nationality*"
            placeholder="Nationality"
            value={insuredDetails.nationalityID}
            error={
              touched?.insuredDetails?.nationalityID
                ? (errors as any)?.insuredDetails?.nationalityID
                : ''
            }
            onBlur={(_, e) => handleBlur(e)}
            onChange={(v) =>
              setFieldValue('insuredDetails.nationalityID', v, true)
            }
            disabled
            selectOptions={nationalityOptions}
            material
          />

          <SelectFormField
            name="insuredDetails.gender"
            title="Gender*"
            placeholder="Gender"
            value={insuredDetails.gender}
            error={
              touched?.insuredDetails?.gender
                ? (errors as any)?.insuredDetails?.gender
                : ''
            }
            onBlur={(_, e) => handleBlur(e)}
            onChange={(v) => setFieldValue('insuredDetails.gender', v, true)}
            disabled
            selectOptions={genderOptions}
            material
          />

          <TextInputFormField
            name="insuredDetails.passportNumber"
            title="Passport Number*"
            placeholder="Passport Number"
            value={insuredDetails.passportNumber}
            error={
              touched?.insuredDetails?.passportNumber
                ? (errors as any)?.insuredDetails?.passportNumber
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(e) =>
              setFieldValue(
                'insuredDetails.passportNumber',
                e.target.value,
                true
              )
            }
            disabled
            material
          />
        </div>

        <div className={classes.fieldRowTwo}>
          <SelectFormField
            name="insuredDetails.occupationClassID"
            title="Occupation Class*"
            placeholder="Occupation Class"
            value={insuredDetails.occupationClassID}
            error={
              touched?.insuredDetails?.occupationClassID
                ? (errors as any)?.insuredDetails?.occupationClassID
                : ''
            }
            onBlur={(_, e) => handleBlur(e)}
            onChange={(v) =>
              setFieldValue('insuredDetails.occupationClassID', v, true)
            }
            disabled
            selectOptions={occupationClassOptions}
            material
          />

          <TextInputFormField
            name="insuredDetails.profession"
            title="Profession*"
            placeholder="Profession"
            value={insuredDetails.profession}
            error={
              touched?.insuredDetails?.profession
                ? (errors as any)?.insuredDetails?.profession
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(e) =>
              setFieldValue('insuredDetails.profession', e.target.value, true)
            }
            disabled
            material
          />
        </div>
      </WidgetSection>
    </WidgetPaper>
  );
};

export { InsuredDetailsWidgetAtom };
